import { z } from 'zod';
import * as dt from './data.types.js';

/**
 * Unassociated (staging) files — uploaded manually or produced by container slot scans.
 */

const BaseResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
});

export const PaginationMetaSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  totalItems: z.number(),
  totalPages: z.number(),
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
});

export const expirationStatusSchema = z.enum(['expiring_soon', 'normal', 'critical']);

export const tempFileSortBySchema = z.enum([
  'uploadedAt',
  'expiresAt',
  'sizeBytes',
  'blurScore',
  'originalName',
  'uploadedByUser',
]);

export const tempFileSourceSchema = z.enum(['manual_upload', 'container_slot_scan']);

export const TempFileListQuerySchema = z.object({
  search: z.string().optional(),
  source: tempFileSourceSchema.optional(),
  uploadedByUserId: z.coerce.number().int().positive().optional(),
  uploadedAfter: z.string().datetime().optional(),
  uploadedBefore: z.string().datetime().optional(),
  expirationStatus: expirationStatusSchema.optional(),
  minBlurScore: z.coerce.number().min(0).max(100).optional(),
  maxBlurScore: z.coerce.number().min(0).max(100).optional(),
  jobId: z.string().optional(),
  efemClientId: z.coerce.number().int().positive().optional(),
  slotNumber: z.coerce.number().int().min(1).max(25).optional(),
  /** When set, limits results to images or non-images. */
  mediaKind: z.enum(['image', 'document', 'any']).default('any'),
  sortBy: tempFileSortBySchema.default('uploadedAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  includeMetadata: z.coerce.boolean().default(true),
});

export const UploadedByUserSchema = z.object({
  id: z.number(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});

export const SlotScanInfoSchema = z.object({
  jobId: z.string(),
  efemClientName: z.string().optional(),
  slotNumber: z.number(),
  totalImagesInScan: z.number().optional(),
});

export const TempFileMetadataSchema = z.object({
  id: z.number(),
  objectKey: z.string(),
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  uploadedAt: z.string().datetime(),
  expiresAt: z.string().datetime().nullable(),
  daysUntilExpiration: z.number(),
  uploadedByUser: UploadedByUserSchema.nullable(),
  thumbnailUrl: z.string().url().optional(),
  fullSizeUrl: z.string().url().optional(),
  metadata: z.object({
    source: tempFileSourceSchema,
    blurScore: z.number().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    jobId: z.string().optional(),
    waferId: z.string().optional(),
    efemClientId: z.number().optional(),
    slotNumber: z.number().optional(),
    thumbnailKey: z.string().optional(),
  }).passthrough(),
  slotScan: SlotScanInfoSchema.optional(),
});

export const TempFileAggregationsSchema = z.object({
  totalFiles: z.number(),
  totalSizeBytes: z.number(),
  bySource: z.object({
    manual_upload: z.number(),
    container_slot_scan: z.number(),
  }),
  expirationStatus: z.object({
    expiring_soon: z.number(),
    normal: z.number(),
    critical: z.number(),
  }),
});

export const TempFileListResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    files: z.array(TempFileMetadataSchema),
    pagination: PaginationMetaSchema,
    aggregations: TempFileAggregationsSchema,
  }),
});

export const TempFileDetailsResponseSchema = BaseResponseSchema.extend({
  data: TempFileMetadataSchema,
});

export const TempFileAssociateSchema = z.object({
  entityType: z.nativeEnum(dt.FILE_ENTITY_TYPE),
  entityId: z.number().int().positive(),
  targetCategory: z.nativeEnum(dt.FILE_CATEGORY).optional(),
});

export const TempFileAssociateResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    fileId: z.number(),
    movedToCategory: z.nativeEnum(dt.FILE_CATEGORY),
    expirationCleared: z.boolean(),
    associatedWith: z.object({
      entityType: z.nativeEnum(dt.FILE_ENTITY_TYPE),
      entityId: z.number(),
    }),
  }),
});

export const TempFileExtendExpirationSchema = z.object({
  additionalDays: z.number().int().min(1).max(90),
});

export const TempFileExtendExpirationResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    fileId: z.number(),
    oldExpiresAt: z.string().datetime(),
    newExpiresAt: z.string().datetime(),
    daysExtended: z.number(),
  }),
});

export const TempFileBulkDeleteSchema = z.object({
  fileIds: z.array(z.number().int().positive()).min(1).max(100),
});

export const TempFileBulkDeleteResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    deleted: z.number(),
    bytesFreed: z.number(),
  }),
});

export const TempFileBulkDownloadSchema = z.object({
  fileIds: z.array(z.number().int().positive()).min(1).max(100),
  organizeBy: z.enum(['jobId', 'uploadDate', 'flat']).default('flat'),
});

function isBrowserFile(value: unknown): boolean {
  const FileCtor = (globalThis as { File?: new (...args: unknown[]) => object }).File;
  return typeof FileCtor === 'function' && value instanceof FileCtor;
}

/** FormData body for POST /tenant/temp-files/upload. */
export const TempFileUploadFormSchema = z.object({
  file: z.custom(isBrowserFile),
});

export const TempFileStatsResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    totalFiles: z.number(),
    totalSizeBytes: z.number(),
    oldestFile: z.string().datetime().nullable(),
    newestFile: z.string().datetime().nullable(),
    bySource: z.object({
      manual_upload: z.object({ count: z.number(), sizeBytes: z.number() }),
      container_slot_scan: z.object({ count: z.number(), sizeBytes: z.number() }),
    }),
    expirationBreakdown: z.object({
      expiringSoon: z.number(),
      normal: z.number(),
      critical: z.number(),
    }),
    qualityDistribution: z.object({
      excellent: z.number(),
      good: z.number(),
      fair: z.number(),
      poor: z.number(),
    }),
  }),
});

export const TempFileUploadResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    fileId: z.number(),
    objectKey: z.string(),
    thumbnailKey: z.string().optional(),
    expiresAt: z.string().datetime(),
  }),
});

export const TempFileIdParamSchema = z.object({
  fileId: z.coerce.number().int().positive(),
});

export const TempFileUploadersResponseSchema = BaseResponseSchema.extend({
  data: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    }),
  ),
});
// Inferred types
export type ExpirationStatus = z.infer<typeof expirationStatusSchema>;
export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;
export type SlotScanInfo = z.infer<typeof SlotScanInfoSchema>;
export type TempFileAggregations = z.infer<typeof TempFileAggregationsSchema>;
export type TempFileAssociate = z.infer<typeof TempFileAssociateSchema>;
export type TempFileAssociateRequest = z.infer<typeof TempFileAssociateSchema>;
export type TempFileAssociateResponse = z.infer<typeof TempFileAssociateResponseSchema>;
export type TempFileBulkDelete = z.infer<typeof TempFileBulkDeleteSchema>;
export type TempFileBulkDeleteRequest = z.infer<typeof TempFileBulkDeleteSchema>;
export type TempFileBulkDeleteResponse = z.infer<typeof TempFileBulkDeleteResponseSchema>;
export type TempFileBulkDownload = z.infer<typeof TempFileBulkDownloadSchema>;
export type TempFileBulkDownloadRequest = z.infer<typeof TempFileBulkDownloadSchema>;
export type TempFileDetailsResponse = z.infer<typeof TempFileDetailsResponseSchema>;
export type TempFileExtendExpiration = z.infer<typeof TempFileExtendExpirationSchema>;
export type TempFileExtendExpirationRequest = z.infer<typeof TempFileExtendExpirationSchema>;
export type TempFileExtendExpirationResponse = z.infer<typeof TempFileExtendExpirationResponseSchema>;
export type TempFileIdParam = z.infer<typeof TempFileIdParamSchema>;
export type TempFileListQuery = z.infer<typeof TempFileListQuerySchema>;
export type TempFileListResponse = z.infer<typeof TempFileListResponseSchema>;
export type TempFileMetadata = z.infer<typeof TempFileMetadataSchema>;
export type TempFileSortBy = z.infer<typeof tempFileSortBySchema>;
export type TempFileSource = z.infer<typeof tempFileSourceSchema>;
export type TempFileStatsResponse = z.infer<typeof TempFileStatsResponseSchema>;
export type TempFileUploadForm = z.infer<typeof TempFileUploadFormSchema>;
export type TempFileUploadResponse = z.infer<typeof TempFileUploadResponseSchema>;
export type TempFileUploadersResponse = z.infer<typeof TempFileUploadersResponseSchema>;
export type UploadedByUser = z.infer<typeof UploadedByUserSchema>;
